import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useTranslation } from '../../../i18n/useTranslation';
import { Dialog } from '../../ui/Dialog/Dialog';
import { Alert } from '../../atoms/Alert/Alert';
import { Button } from '../../atoms/Button/Button';
import { Heading } from '../../atoms/Typography/Heading';
import { Text } from '../../atoms/Typography/Text';
import { Stack } from '../../atoms/Stack/Stack';
import { ErrorBoundary } from '../../errors/ErrorBoundary';
import { ApiError } from '../../../lib/apiClient';
import { formatLegalDate } from '../../../api/legal';
import { PriorityNoticeContext, type PriorityNoticeState } from '../../../contexts/PriorityNoticeContext';
import {
  acknowledgePrivacyPolicy,
  fetchPrivacyPolicyStatus,
  type PrivacyPolicyStatus,
} from '../../../services/legalStatusService';
import { LEGAL_PATHS } from '../LegalLinks';
import './PrivacyPolicyNotice.css';

/**
 * Per-tab memory of a notice closed without acknowledging (× / Escape): it is
 * not shown again in this session, but at the next sign-in (sign-out clears
 * sessionStorage, lib/userSession, and this component removes the key too).
 */
export const PRIVACY_NOTICE_DISMISSED_KEY = 'reefguide-privacy-notice-dismissed';
/** Give up waiting for the status after this long — never hold other dialogs back for ever. */
const STATUS_TIMEOUT_MS = 10_000;

function readDismissed(): string | null {
  try {
    return sessionStorage.getItem(PRIVACY_NOTICE_DISMISSED_KEY);
  } catch {
    return null;
  }
}

function writeDismissed(version: string | null): void {
  try {
    if (version === null) sessionStorage.removeItem(PRIVACY_NOTICE_DISMISSED_KEY);
    else sessionStorage.setItem(PRIVACY_NOTICE_DISMISSED_KEY, version);
  } catch {
    // storage unavailable — the in-memory state still closes the notice for this page load
  }
}

/** End-to-end suites drive automated browsers with long-lived test accounts; never block them with a modal. */
function isAutomatedBrowser(): boolean {
  try {
    return typeof navigator !== 'undefined' && navigator.webdriver === true;
  } catch {
    return false;
  }
}

type Phase = 'idle' | 'checking' | 'open' | 'done';

interface OptionalAuthState {
  isAuthenticated?: boolean;
  isLoading?: boolean;
  user?: { email?: string } | null;
}

/**
 * The auth state, or null without an AuthProvider (useAuth throws there) or
 * with a partial test mock of the auth module. useAuth always reads the same
 * context first, so catching its error keeps the hook order stable.
 */
function useOptionalAuthState(): OptionalAuthState | null {
  try {
    return (useAuth() as { state?: OptionalAuthState } | null)?.state ?? null;
  } catch {
    return null;
  }
}

/** Fallback of a crashed notice: closes it, so other dialogs are not held back. */
function CloseOnCrash({ onCrash }: { onCrash: () => void }) {
  useEffect(() => {
    onCrash();
  }, [onCrash]);
  return null;
}

interface NoticeDialogProps {
  status: PrivacyPolicyStatus;
  saving: boolean;
  saveFailed: boolean;
  onAcknowledge: () => void;
  onDismiss: () => void;
}

function PrivacyPolicyNoticeDialog({ status, saving, saveFailed, onAcknowledge, onDismiss }: NoticeDialogProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const date = (iso: string): string => formatLegalDate(iso, locale);
  const changes = status.changes;
  const changesTitleId = useId();

  return (
    <Dialog
      isOpen
      onClose={onDismiss}
      title={t('legal.updateNotice.title')}
      size="md"
      data-testid="privacy-policy-notice"
      footer={
        <div className="rg-privacy-notice__footer">
          <Button variant="primary" onClick={onAcknowledge} isLoading={saving} disabled={saving}>
            {t('legal.updateNotice.acknowledge')}
          </Button>
        </div>
      }
    >
      <Stack gap="4">
        <Text>{t(changes.length > 0 ? 'legal.updateNotice.intro' : 'legal.updateNotice.introNoSummary')}</Text>
        <Text variant="muted" data-testid="privacy-policy-notice-effective">
          {t('legal.updateNotice.effective', { date: date(status.effectiveDate), version: status.currentVersion })}
        </Text>
        {changes.length > 0 && (
          <section className="rg-privacy-notice__changes" aria-labelledby={changesTitleId}>
            <Heading level={3} id={changesTitleId} className="rg-privacy-notice__changes-title">
              {t('legal.updateNotice.changesTitle')}
            </Heading>
            <ul className="rg-privacy-notice__list">
              {changes.map((change) => (
                <li key={change.version} className="rg-privacy-notice__item">
                  {changes.length > 1 && (
                    <Text as="span" variant="label" className="rg-privacy-notice__version">
                      {t('legal.updateNotice.changeVersion', { version: change.version, date: date(change.effectiveDate) })}
                    </Text>
                  )}
                  <Text as="span">{change.summary}</Text>
                </li>
              ))}
            </ul>
          </section>
        )}
        <Text>
          <a className="rg-privacy-notice__link" href={LEGAL_PATHS.privacy} target="_blank" rel="noopener noreferrer">
            {t('legal.updateNotice.readPolicy')} {t('legal.opensInNewTab')}
          </a>
        </Text>
        <Text variant="muted">{t('legal.updateNotice.informationOnly')}</Text>
        {saveFailed && (
          <Alert tone="danger" compact>
            {t('legal.updateNotice.saveFailed')}
          </Alert>
        )}
      </Stack>
    </Dialog>
  );
}

/**
 * Privacy policy update notice — the app's priority notice.
 *
 * Mounted once around the routed app (App.tsx). After sign-in, or on app start
 * with a valid session, it asks GET /api/v1/me/legal-status; when the policy in
 * force is newer than the version the user acknowledged it opens a modal Dialog
 * with the change summary, the effective date and a link to the full policy.
 * "Zur Kenntnis genommen" records the acknowledgement (information under
 * Art. 13 GDPR, not consent — no checkbox, nothing pre-ticked). Closing it
 * otherwise records nothing; it shows again at the next sign-in.
 *
 * Provides PriorityNoticeContext to its children: other auto-opening dialogs
 * read `usePriorityNoticeActive()` and stay closed while this notice is being
 * checked or is open, so two dialogs never stack.
 *
 * Renders only its children (and provides `active: false`) without an
 * AuthProvider, when signed out, in automated browsers, or when the status
 * cannot be loaded.
 */
export function PrivacyPolicyNotice({ children }: { children?: ReactNode }) {
  const authState = useOptionalAuthState();
  const { i18n } = useTranslation();
  const signedIn = authState?.isAuthenticated === true;
  const ready = signedIn && authState?.isLoading !== true;
  const userKey = authState?.user?.email ?? '';
  const languageRef = useRef(i18n.resolvedLanguage ?? i18n.language ?? 'en');
  languageRef.current = i18n.resolvedLanguage ?? i18n.language ?? 'en';

  const [phase, setPhase] = useState<Phase>('idle');
  const [status, setStatus] = useState<PrivacyPolicyStatus | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  // Signed out: forget everything, including a dismissal — the next sign-in shows it again.
  useEffect(() => {
    if (signedIn) return;
    writeDismissed(null);
    setPhase('idle');
    setStatus(null);
    setSaving(false);
    setSaveFailed(false);
  }, [signedIn]);

  useEffect(() => {
    if (!ready) return undefined;
    if (isAutomatedBrowser()) {
      setPhase('done');
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), STATUS_TIMEOUT_MS);
    let settled = false;
    const finish = (next: PrivacyPolicyStatus | null): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (next && next.needsAcknowledgement && readDismissed() !== next.currentVersion) {
        setStatus(next);
        setSaveFailed(false);
        setPhase('open');
      } else {
        setPhase('done');
      }
    };
    setPhase('checking');
    fetchPrivacyPolicyStatus(languageRef.current, controller.signal)
      .then(finish)
      .catch(() => finish(null));
    controller.signal.addEventListener('abort', () => finish(null));
    return () => {
      settled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [ready, userKey]);

  const dismiss = useCallback(() => {
    if (saving) return;
    writeDismissed(status?.currentVersion ?? null);
    setPhase('done');
  }, [saving, status]);

  const acknowledge = useCallback(() => {
    if (!status || saving) return;
    setSaving(true);
    setSaveFailed(false);
    acknowledgePrivacyPolicy(status.currentVersion)
      .then(() => setPhase('done'))
      .catch((err: unknown) => {
        // 409: a newer version was published meanwhile — the next sign-in shows that one.
        if (err instanceof ApiError && err.status === 409) setPhase('done');
        else setSaveFailed(true);
      })
      .finally(() => setSaving(false));
  }, [status, saving]);

  const closeAfterCrash = useCallback(() => setPhase('done'), []);

  const value = useMemo<PriorityNoticeState>(
    () => ({ active: signedIn && (phase === 'checking' || phase === 'open') }),
    [signedIn, phase],
  );

  return (
    <PriorityNoticeContext.Provider value={value}>
      {children}
      {signedIn && phase === 'open' && status !== null && (
        <ErrorBoundary fallback={() => <CloseOnCrash onCrash={closeAfterCrash} />}>
          <PrivacyPolicyNoticeDialog
            status={status}
            saving={saving}
            saveFailed={saveFailed}
            onAcknowledge={acknowledge}
            onDismiss={dismiss}
          />
        </ErrorBoundary>
      )}
    </PriorityNoticeContext.Provider>
  );
}

export default PrivacyPolicyNotice;
